import React, {useEffect, useMemo, useRef, useState} from 'react'
import {Navigate, useLocation, useSearchParams} from "react-router-dom";
import {useQueryClient} from '@tanstack/react-query';
import {useMount} from "react-use";
import Switch from "react-switch";
import { Dropdown, Empty, Pagination, Switch as AntSwitch } from 'antd';
import Page from "../Page";
import EmptyBanner from "../../components/EmptyBanner";
import CardList from "../../components/CardList";
import LoadingBanner from "../../components/LoadingBanner";
import ErrorBanner from "../../components/ErrorBanner";
import TablePagination from "../../components/TablePagination";
import PageNotAllowed from '../../components/errors/PageNotAllowed';

import JobPositionPanel from "./components/JobPositionPanel";
import JobBoardFilter from "./components/JobBoardFilter";
import JobDetails from "./JobDetails";
import ProjectPosition from "../../models/ProjectPosition";
import {useAuth} from "../../providers/AuthProvider";
import {useAPI} from "../../config/API";
import Project from "../../models/Project";
import AppButton from "../../components/AppButton";
import JobBoardCard, { JobBoardCardSkeleton } from "./components/JobBoardCard";
import JobBoardFilterDropdown, { FilterTrigger } from "./components/JobBoardFilterDropdown";

// TODO Remove filter for job placement later
const ContractTypes = TB.filter(
    TB.map(
        laravel.project_contract_types || {},
        (name, value) => ({value: parseInt(value), name})
    ),
    ({name, value}) => name !== 'Job Placement'
)

console.log('CONTRACT_TYPES', ContractTypes)
const DisciplineCategories = TB.map([...laravel.discipline_categories], cat => {
    let option = TB.pick(cat, ['id', 'name', 'disciplines', 'display_order']);
    // TODO Change with updated discipline filters for client/talent
    let disciplines = cat.disciplines;//TB.filter(cat.disciplines, ['is_applicable', true]);
    option.disciplines = TB.map(disciplines, discipline => TB.pick(discipline, ['id', 'name', 'display_order']))
    option.value = option.id;

    return option
});

const Disciplines = TB.map(DisciplineCategories, category => {
    return {
        ...category,
        options: category.disciplines.map(discipline => {
            return {
                ...discipline,
                value: discipline.id
            }
        })
    }
})


const TimeZones = TB.TimeZonesSplit()

const PAGE_COUNT = 15;

export default class TalentJobBoard extends Page {

    static nestedPages = []

    static path = '/job-board';

    static defaultPath() {
        return '/talent' + this.path
    }

    static pathForJob(job) {
        return JobDetails.pathForJob(job)
    }

    /*
    |--------------------------------------------------------------------------
    | Sorting
    |--------------------------------------------------------------------------
    */

    static sortTypes = {
        posted_at_desc: 'Date Posted (new to old)',
        posted_at_asc: 'Date Posted (old to new)',
        payout_amount_desc: 'Est Payout (high to low)',
        payout_amount_asc: 'Est Payout (low to high)',
        client_name_asc: 'Client (A to Z)',
        client_name_desc: 'Client (Z to A)',
        proximity_asc: 'Location (near to far)',
    };

    defaultSortBy() {
        return TB.keys(this.constructor.sortTypes)[0]
    }

    /*
    |--------------------------------------------------------------------------
    | Local Storage
    |--------------------------------------------------------------------------
    */

    storageKey() {
        return `jobBoardParams`
    }

    persistStorage() {
        let filters = {...this.state.filters};
        if (filters.discipline_categories) {
            filters.discipline_categories = TB.map(filters.discipline_categories, 'id')
        }
        if (filters.disciplines) {
            filters.disciplines = TB.map(filters.disciplines, 'id')
        }
        if (filters.contract_types) {
            console.log('persistStorage', filters, filters.contract_types)
            filters.contract_types = TB.map(filters.contract_types, 'value')
            console.log('persistStorage', filters, filters.contract_types)
        }

        localStorage.setItem(this.storageKey(), JSON.stringify(
            {filters}
        ));
    }

    filtersFromStorage(filters) {
        if (!filters) {
            let storage = JSON.parse(
                localStorage.getItem(this.storageKey())
                || '{}'
            );

            filters = storage.filters || {};
        }

        if (filters.disciplines) {
            filters.disciplines = TB.map(
                filters.disciplines,
                id => TB.find(TB.flatten(TB.map(Disciplines, 'options')), ['id', parseInt(id)])
            )
        } else {
            filters.disciplines = TB.flatten(TB.map(Disciplines, 'options'));
        }

        if (!filters.time_zones) {
            filters.time_zones = [...TimeZones[0].options, ...TimeZones[1].options]
        }
        if (filters.contract_types) {
            filters.contract_types = TB.map(
                filters.contract_types,
                id => TB.find(ContractTypes, ['value', parseInt(id)])
            )
        }else {
            filters.contract_types = [...ContractTypes]
        }

        if (!filters.sort_by) {
            filters.sort_by = this.defaultSortBy();
        }
        if (!filters.per_page) {
            filters.per_page = PAGE_COUNT;
        }

        return filters
    }

    resetState() {

        let filters = this.filtersFromStorage(false);

        return this.promisedState({
            isLoading: false,
            filters,
            appliedFilters: {
                ...filters
            },
            lastPage: null,
            currentPage: null,
            total: null,
            formErrors: null,
        })
    }

    constructor(props) {
        super(props);

        let filters = {
            ...this.filtersFromStorage()
        };

        if (TB.has(props, 'location.state.only_matching')) {
            // reset filters
            filters = {
                ...this.filtersFromStorage({})
            };

            filters.only_matching = props.location.state.only_matching;
        }

        this.state = {
            isLoading: false,
            filters,
            appliedFilters: {
                ...filters
            },
            lastPage: null,
            currentPage: null,
            total: null,
            formErrors: null,
        };
    }

    async componentDidMount() {
        super.componentDidMount();
    }

    /*
    |--------------------------------------------------------------------------
    | Job Helper Methods
    |--------------------------------------------------------------------------
    */

    async toggleBrowseType() {
        let filters = {
            ...this.state.filters
        };

        let matching = !filters.only_matching;
        filters.only_matching = matching ? 1 : 0;

        await this.promisedState({filters});

        return this.applyFilters(true);
    }

    /*
    |--------------------------------------------------------------------------
    | Requests / Job Methods
    |--------------------------------------------------------------------------
    */
    async onRequest(callback) {
        await this.promisedState({isDisabled: true});

        let result = await callback().catch(e => console.warn('ERROR: onRequest', e))

        await this.promisedState({isDisabled: false});

        return result;
    }

    /*
    |--------------------------------------------------------------------------
    | Filters
    |--------------------------------------------------------------------------
    */

    canApplyFilters() {
        let filters = TB.pickBy({...this.state.filters}, filter =>
            TB.isArray(filter) ? filter : filter
        );
        let appliedFilters = TB.pickBy({...this.state.appliedFilters}, filter =>
            TB.isArray(filter) ? filter : filter
        );

        return !TB.isEqual(appliedFilters, filters)
    }

    canClearFilters() {
        let filters = TB.filter({...this.state.filters}, filter => TB.isArray(filter) && filter.length > 0);

        return Object.keys(filters).length > 0
    }

    async clearFilters() {
        let filters = {
            sort_by: this.state.filters.sort_by || this.defaultSortBy()
        };

        await this.promisedState({
            filters
        })

        await this.persistStorage();

        return this.resetState();
    }

    async editFilter(editingFilter) {
        if (this.state.editingFilter === editingFilter) {
            editingFilter = null;
        }
        return this.promisedState({editingFilter});
    }

    async validateFilters(filters, showError = false) {

        let isLoading = false, formErrors = {}

        let valid = Object.keys(formErrors).length === 0;
        if (!valid && showError) {
            await this.promisedState({
                isLoading,
                formErrors,
                jobs: null,
            })
        }

        return valid
    }

    async applyFilters(resetPage = true) {
        await this.promisedState({
            editingFilter: null,
            // isLoading: true,
            // error: null,
            // formErrors: null,
        })

        let filters = {...this.state.filters};

        if (resetPage) {
            filters.page = 1;
        } else {
            filters.page = undefined;
        }
        await this.promisedState({
            filters
        });

        let valid = await this.validateFilters(this.state.filters, true);
        if (!valid) {
            return
        }

        await this.persistStorage();

        // await this.searchJobs(filters)

        return this.promisedState({appliedFilters: {...this.state.filters}})
    }

    onChangeFilters(changed = {}) {

        // let appliedFilters = this.filtersFromStorage();

        return this.promisedState({
            filters: {
                ...this.state.filters,
                ...changed
            }
        })
    }

    mapFilters(filters = {}) {

        let {
            disciplines,
            time_zones,
            contract_types,
            sort_by,
            sort_dir,
            ...mapFilters
        } = {...filters}

        if (sort_by && TB.endsWith(sort_by, '_asc')) {
            sort_by = TB.replace(sort_by, '_asc', '');
            sort_dir = 'asc'
        }
        if (sort_by && TB.endsWith(sort_by, '_desc')) {
            sort_by = TB.replace(sort_by, '_desc', '');
            sort_dir = 'desc'
        }

        mapFilters.disciplines = TB.map(disciplines, 'value').join(',');
        mapFilters.time_zones = TB.map(time_zones, 'value').join(',');
        mapFilters.contract_types = TB.map(contract_types, 'value').join(',');

        return {
            ...mapFilters,
            sort_dir,
            sort_by
        }
    }

    createDefaultFilter(editingFilter, options, selected) {

        if (!TB.isNaN(parseInt(selected))) {
            selected = parseInt(selected)
        }
        let selectedOption = TB.find(options, option => TB.isEqual(option.value, selected))
        if (!selectedOption) {
            selectedOption = TB.first(options);
        }

        return (
            <Dropdown
                menu={{
                    items: options,
                    selectedKeys: [selected],
                    onClick: e => this.onChangeFilters({ [editingFilter]: e.key }),
                    // disabled: props.disabled,
                }} 
                trigger={['click']}
                autoFocus
                disabled={this.state.isLoading}
            >
                <button
                    type="button"
                    onClick={e => e.preventDefault()}
                    className={TB.mapClass("dropdown-toggle filter-button filter-sm", {
                        'disabled': this.state.isLoading
                    })}
                >
                    <span>{selectedOption.display || selectedOption.name}</span>
                </button>
            </Dropdown>
        )
    }

    createFilter(label, subLabel, editingFilter, options) {
        let value = [...(this.state.filters[editingFilter] || [])];
        return (
            <JobBoardFilter
                key={label}
                value={value}
                options={TB.isArray(options) ? options : undefined}
                label={label}
                subLabel={subLabel}
                right={false}
                onToggleEditing={() => this.editFilter(editingFilter)}
                isEditing={this.state.editingFilter === editingFilter}
                formErrors={this.state.formErrors && this.state.formErrors[editingFilter]}
                isDisabled={this.state.isLoading || this.state.isDisabled}
                onChange={async (items) => {
                    if (TB.keys(this.state.formErrors).length) {
                        await this.promisedState({formErrors: null})
                    }

                    return this.onChangeFilters({
                        [editingFilter]: items
                    })
                }}
            />
        )
    }

    renderFilters() {
        let {filters, isLoading} = this.state;

        return (
            <div className="job-board-filters-container">

                <div className="type-filters">

                    <div className="row-content">
                        <div className="form-group">
                            <label htmlFor="type-toggle"
                                   className={isLoading ? "pointer-events-none" : ''}>
                                <span>
                                    Show Only My Matches
                                </span>

                                <Switch
                                    height={25}
                                    width={55}
                                    style={{transform: [{scaleX: .7}, {scaleY: .7}]}}
                                    checked={!!this.state.filters.only_matching}
                                    onChange={this.toggleBrowseType.bind(this)}
                                    onColor={TB.config('color.success-dark')}
                                    offColor={TB.config('color.disabled')}
                                    uncheckedIcon={<p className="job-board-switch">No</p>}
                                    checkedIcon={<p className="job-board-switch">Yes</p>}
                                    activeBoxShadow={'0 0 0.5px 1px ' + TB.config('color.success-dark')}
                                    className="react-switch"
                                    id="type-toggle"
                                />

                            </label>
                        </div>


                    </div>

                </div>

                <div className="criteria-filters">

                    <div className="row-content">

                        {this.createFilter(
                            'Role',
                            null,
                            'disciplines',
                            Disciplines
                        )}
                        {this.createFilter(
                            'Location',
                            'Time Zones',
                            'time_zones',
                            TimeZones
                        )}
                        {this.createFilter(
                            'Contract Type',
                            null,
                            'contract_types',
                            ContractTypes
                        )}
                        {this.createDefaultFilter(
                            'sort_by',
                            TB.map(this.constructor.sortTypes, (name, value) => {
                                return {
                                    label: name,
                                    key: value,
                                    name, value, display: (
                                        <span>
                                            Sort By: {name}
                                        </span>
                                    )
                                }
                            }),
                            this.state.filters.sort_by || this.defaultSortBy()
                        )}
                    </div>

                </div>

                <div className="filter-actions">
                    <div className="row-content">
                        <button onClick={this.applyFilters.bind(this)}
                                className="mx-2 btn btn-light btn-primary apply-filters-btn"
                                disabled={isLoading || !this.canApplyFilters() || this.state.isDisabled}>
                            Search
                        </button>

                        <button onClick={this.clearFilters.bind(this)}
                                className="mx-2 btn btn-dark apply-filters-btn"
                                disabled={isLoading || !this.canClearFilters() || this.state.isDisabled}>
                            Clear
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    hasInvalidFilters() {
        let {
            formErrors
        } = this.state;

        return TB.isDefined(formErrors) && Object.keys(formErrors).length > 0;
    }

    /*
    |--------------------------------------------------------------------------
    | Render Methods
    |--------------------------------------------------------------------------
    */

    renderJobBoardPage() {
        return (
            <div className="client-portal-container job-board-container">
                <div className='client-portal-page-container pb-0 pt-3 portal-full'>

                    <div className="job-board-container">
                        <div className="job-board-header">

                            <h4>
                                Welcome to Wripple’s Job Board!
                            </h4>
                            <p>Browse highlighted open jobs and if you have signed up for the role requested, let
                                clients know you’re interested, otherwise refer a friend. Use the search tool to zero in
                                on specific opportunities.</p>

                        </div>

                        {this.renderFilters()}

                        <JobBoardPage
                            filters={this.state.appliedFilters}
                            disabled={this.state.isDisabled}
                            onRequest={this.onRequest.bind(this)}
                            onToggleLoading={isLoading => {
                                console.log('toggleLoading', isLoading)
                                return this.promisedState({isLoading})
                            }}
                        />
                    </div>


                </div>

            </div>
        );
    }

    render() {
        if (this.context.auth?.isTalentSupplierResource()) {
            return <PageNotAllowed redirect="/talent/dashboard" />
        }

        return (
            <React.Fragment>
                {this.renderJobBoardPage()}
            </React.Fragment>
        )
    }
}

const JobBoardPage = props => {

    const queryClient = useQueryClient();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();

    // const [currentPage, setCurrentPage] = useState(1);
    const currentPage = useMemo(() => parseInt(searchParams.get('page') ) || 1, [searchParams?.toString()]);

    const setCurrentPage = (pageNumber) => {
        let params = new URLSearchParams(searchParams);

        if (TB.isNaN(parseInt(pageNumber))) {
            return
        }
        if (TB.isNaN(parseInt(params.get('page'))) || parseInt(params.get('page')) !== parseInt(pageNumber)) {
            params.set('page', pageNumber);

            setSearchParams(params,{preventScrollReset:true});
        }
    }

    useMount(() => {
        console.log('mount')
        if (!TB.isNaN(parseInt(props.filters.page)) && !searchParams.has('page')) {
            setCurrentPage(props.filters.page)
        }
    })
    const getParams = () => {
        let {
            disciplines,
            time_zones,
            sort_by,
            sort_dir,
            contract_types,
            ...mapFilters
        } = {...props.filters}

        if (sort_by && TB.endsWith(sort_by, '_asc')) {
            sort_by = TB.replace(sort_by, '_asc', '');
            sort_dir = 'asc'
        }
        if (sort_by && TB.endsWith(sort_by, '_desc')) {
            sort_by = TB.replace(sort_by, '_desc', '');
            sort_dir = 'desc'
        }

        mapFilters.disciplines = TB.map(disciplines, 'value').join(',');
        mapFilters.time_zones = TB.map(time_zones, 'value').join(',');
        mapFilters.contract_types = TB.map(contract_types, 'value').join(',');

        return {
            ...mapFilters,
            page: currentPage,
            sort_dir,
            sort_by
        }
    }

    const [appliedParams, setAppliedParams] = useState(getParams());

    const {
        data,
        isFetching,
        isLoading,
        isRefetching,
        error,
        refetch,
        ...result
    } = useAPI(['fetchJobBoardForTalent', getParams()], {
            onSuccess: async (data) => {
                console.log('onSuccess', data);

                if (!TB.isNaN(parseInt(currentPage)) && data.last_page < parseInt(currentPage)) {

                    console.log('onSuccessreset')
                    setCurrentPage(1);
                }
                setAppliedParams(getParams())
            }
        }
    )

    // useEffect(() => {
    //     if (props.filters?.page === 1) {
    //         setCurrentPage(1)
    //     }
    // }, [props.filters?.page]);


    useEffect(() => {
        props.onToggleLoading(isFetching || isRefetching)
    }, [isFetching, isRefetching]);

    const updateJob = async (updatedJob) => {
        return queryClient.setQueryData(
            ['fetchJobBoardForTalent', getParams()],
            prevData => {
                if (!prevData) return undefined;

                return {
                    ...prevData,
                    jobs: TB.map([...(prevData?.jobs || [])], job => {
                        if (job.id === updatedJob.id) {
                            return ProjectPosition.make({...job, ...updatedJob});
                        }
                        return job
                    })
                };
            }
        );
    }


    const {
        last_page: lastPage,
        total: totalMatches,
        jobs
    } = data || {};
    return (
        <React.Fragment>


            {lastPage > 1 && (
                <div className="mb-3 container-lg">
                    <TablePagination
                        isDisabled={props.disabled || isFetching || isRefetching || isLoading}
                        handlePageClick={setCurrentPage}
                        currentPage={currentPage}
                        pagesCount={lastPage}
                        total={totalMatches}
                        perPage={PAGE_COUNT}/>
                </div>
            )}


            {isFetching ? (
                <LoadingBanner message={'Loading'}/>
            ) : (
                TB.isObject(error) ? (
                    <ErrorBanner {...error} onRetry={refetch}/>
                ) : (
                    <div className="job-list full-card-list">
                        {jobs?.length === 0 ? (
                            <EmptyBanner
                                style={{paddingTop: '200px'}}
                                message={`No Jobs Found`}/>
                        ) : (
                            <div className="jobs-group-container">
                                <CardList
                                    maxItemsPerRow={3}
                                    maxItemWidth={400}
                                    items={jobs.map(job => (
                                            <JobPositionPanel
                                                key={'job-' + job.id}
                                                job={job}
                                                detailLink={TalentJobBoard.pathForJob(job)}
                                                disabled={isFetching || isLoading || props.disabled}
                                                onRequest={props.onRequest.bind(this)}
                                                onUpdateJob={updateJob}
                                            />

                                        )
                                    )}/>
                            </div>
                        )}
                    </div>

                )
            )}

            {lastPage > 1 && (
                <div className="mb-3 container-lg">
                    <TablePagination
                        isDisabled={props.disabled || isFetching || isRefetching || isLoading}
                        handlePageClick={setCurrentPage}
                        currentPage={currentPage}
                        pagesCount={lastPage}
                        total={totalMatches}
                        perPage={PAGE_COUNT}/>
                </div>
            )}

        </React.Fragment>
    );
};

JobBoardPage.propTypes = {};

/* ============================================================================
 * Redesigned job board (functional / Tailwind)
 * Same filters, local storage, URL paging and API query as TalentJobBoard + JobBoardPage.
 * ========================================================================== */

const JOB_BOARD_STORAGE_KEY = 'jobBoardParams';
const DEFAULT_SORT = TB.keys(TalentJobBoard.sortTypes)[0];

// Short labels for the sort trigger (menu still shows the full TalentJobBoard.sortTypes names)
const SORT_SHORT_LABELS = {
    posted_at_desc: 'Newest',
    posted_at_asc: 'Oldest',
    payout_amount_desc: 'Highest pay',
    payout_amount_asc: 'Lowest pay',
    client_name_asc: 'Client A–Z',
    client_name_desc: 'Client Z–A',
    proximity_asc: 'Nearest',
};

const SKELETON_CARD_COUNT = 6;

const ALL_DISCIPLINE_OPTIONS = TB.flatten(TB.map(Disciplines, 'options'));

// Same as TalentJobBoard.filtersFromStorage()
const jobBoardFiltersFromStorage = (filters) => {
    if (!filters) {
        let storage = {};
        try {
            storage = JSON.parse(localStorage.getItem(JOB_BOARD_STORAGE_KEY) || '{}');
        } catch (e) {
            console.warn(e);
        }
        filters = storage.filters || {};
    }

    filters = { ...filters };

    filters.disciplines = filters.disciplines
        ? TB.compact(TB.map(filters.disciplines, id => TB.find(ALL_DISCIPLINE_OPTIONS, ['id', parseInt(id)])))
        : [...ALL_DISCIPLINE_OPTIONS];

    if (!filters.time_zones) {
        filters.time_zones = [...TimeZones[0].options, ...TimeZones[1].options];
    }

    filters.contract_types = filters.contract_types
        ? TB.compact(TB.map(filters.contract_types, id => TB.find(ContractTypes, ['value', parseInt(id)])))
        : [...ContractTypes];

    if (!filters.sort_by) filters.sort_by = DEFAULT_SORT;
    if (!filters.per_page) filters.per_page = PAGE_COUNT;

    return filters;
};

// Same as TalentJobBoard.persistStorage()
const persistJobBoardFilters = (filters) => {
    const stored = { ...filters };
    if (stored.discipline_categories) stored.discipline_categories = TB.map(stored.discipline_categories, 'id');
    if (stored.disciplines) stored.disciplines = TB.map(stored.disciplines, 'id');
    if (stored.contract_types) stored.contract_types = TB.map(stored.contract_types, 'value');

    try {
        localStorage.setItem(JOB_BOARD_STORAGE_KEY, JSON.stringify({ filters: stored }));
    } catch (e) {
        console.warn(e);
    }
};

// Same as JobBoardPage.getParams()
const jobBoardQueryParams = (filters, page) => {
    let { disciplines, time_zones, sort_by, sort_dir, contract_types, ...params } = { ...filters };

    if (sort_by && TB.endsWith(sort_by, '_asc')) {
        sort_by = TB.replace(sort_by, '_asc', '');
        sort_dir = 'asc';
    }
    if (sort_by && TB.endsWith(sort_by, '_desc')) {
        sort_by = TB.replace(sort_by, '_desc', '');
        sort_dir = 'desc';
    }

    params.disciplines = TB.map(disciplines, 'value').join(',');
    params.time_zones = TB.map(time_zones, 'value').join(',');
    params.contract_types = TB.map(contract_types, 'value').join(',');

    return { ...params, page, sort_dir, sort_by };
};

const compactFilters = filters => TB.pickBy({ ...filters }, filter => filter);

const PAGINATION_CLASSES = [
    '[&_.ant-pagination-item]:rounded-lg [&_.ant-pagination-item]:border-gray-200 [&_.ant-pagination-item]:font-semibold',
    '[&_.ant-pagination-item-active]:border-primary [&_.ant-pagination-item-active]:bg-primary [&_.ant-pagination-item-active_a]:text-white',
    '[&_.ant-pagination-prev_.ant-pagination-item-link]:rounded-lg [&_.ant-pagination-next_.ant-pagination-item-link]:rounded-lg',
].join(' ');

const TalentJobBoardNew = () => {
    const { auth } = useAuth();
    const location = useLocation();
    const queryClient = useQueryClient();
    const [searchParams, setSearchParams] = useSearchParams();

    // Initial filters: local storage, or a reset + only_matching when linked from the dashboard
    const initialFilters = useRef(null);
    if (!initialFilters.current) {
        let initial = jobBoardFiltersFromStorage();
        if (TB.has(location, 'state.only_matching')) {
            initial = jobBoardFiltersFromStorage({});
            initial.only_matching = location.state.only_matching;
        }
        initialFilters.current = initial;
    }

    const [filters, setFilters] = useState(initialFilters.current);
    const [appliedFilters, setAppliedFilters] = useState({ ...initialFilters.current });
    const [isDisabled, setIsDisabled] = useState(false);

    /* -------------------------------- Paging (URL ?page=) -------------------------------- */

    const currentPage = useMemo(() => parseInt(searchParams.get('page')) || 1, [searchParams?.toString()]);

    const setCurrentPage = (pageNumber) => {
        if (TB.isNaN(parseInt(pageNumber))) return;

        const params = new URLSearchParams(searchParams);
        if (TB.isNaN(parseInt(params.get('page'))) || parseInt(params.get('page')) !== parseInt(pageNumber)) {
            params.set('page', pageNumber);
            setSearchParams(params, { preventScrollReset: true });
        }
    };

    useMount(() => {
        if (!TB.isNaN(parseInt(appliedFilters.page)) && !searchParams.has('page')) {
            setCurrentPage(appliedFilters.page);
        }
    });

    /* -------------------------------- Query -------------------------------- */

    const queryParams = useMemo(() => jobBoardQueryParams(appliedFilters, currentPage), [appliedFilters, currentPage]);

    const { data, isFetching, isLoading, isRefetching, error, refetch } = useAPI(['fetchJobBoardForTalent', queryParams], {
        onSuccess: (result) => {
            if (!TB.isNaN(parseInt(currentPage)) && result?.last_page < parseInt(currentPage)) {
                setCurrentPage(1);
            }
        },
    });

    const isBusy = isFetching || isRefetching || isLoading;

    const updateJob = async (updatedJob) => queryClient.setQueryData(
        ['fetchJobBoardForTalent', queryParams],
        prevData => {
            if (!prevData) return undefined;
            return {
                ...prevData,
                jobs: TB.map([...(prevData?.jobs || [])], job => (
                    job.id === updatedJob.id ? ProjectPosition.make({ ...job, ...updatedJob }) : job
                )),
            };
        }
    );

    const onRequest = async (callback) => {
        setIsDisabled(true);
        const result = await callback().catch(e => console.warn('ERROR: onRequest', e));
        setIsDisabled(false);
        return result;
    };

    /* -------------------------------- Filters -------------------------------- */

    const changeFilters = changed => setFilters(current => ({ ...current, ...changed }));

    const applyFilters = (nextFilters = filters, resetPage = true) => {
        const applied = { ...nextFilters, page: resetPage ? 1 : undefined };

        setFilters(applied);
        persistJobBoardFilters(applied);
        setAppliedFilters({ ...applied });

        if (resetPage) setCurrentPage(1);
    };

    const toggleOnlyMatching = () => applyFilters({ ...filters, only_matching: filters.only_matching ? 0 : 1 });

    const canApplyFilters = !TB.isEqual(compactFilters(appliedFilters), compactFilters(filters));
    const canClearFilters = TB.filter({ ...filters }, filter => TB.isArray(filter) && filter.length > 0).length > 0;

    const clearFilters = () => {
        persistJobBoardFilters({ sort_by: filters.sort_by || DEFAULT_SORT });

        const reset = jobBoardFiltersFromStorage(false);
        setFilters(reset);
        setAppliedFilters({ ...reset });
    };

    const controlsDisabled = isBusy || isDisabled;

    const sortItems = TB.map(TalentJobBoard.sortTypes, (name, key) => ({ key, label: name }));
    const sortBy = filters.sort_by || DEFAULT_SORT;

    /* -------------------------------- Render -------------------------------- */

    const { last_page: lastPage, total, per_page: perPage, jobs } = data || {};
    const pageSize = parseInt(perPage) || PAGE_COUNT;
    const showingFrom = jobs?.length ? ((currentPage - 1) * pageSize) + 1 : 0;
    const showingTo = jobs?.length ? showingFrom + jobs.length - 1 : 0;

    const pagination = lastPage > 1 && (
        <Pagination
            current={currentPage}
            total={total}
            pageSize={pageSize}
            onChange={setCurrentPage}
            disabled={controlsDisabled}
            showSizeChanger={false}
            showLessItems
            className={PAGINATION_CLASSES}
        />
    );

    if (auth?.isTalentSupplierResource()) {
        return <PageNotAllowed redirect="/talent/dashboard" />;
    }

    return (
        <div className="mx-auto w-full max-w-[1450px] px-4 py-6 sm:px-6 md:py-10">
            <header className="mb-5 md:mb-6">
                <h1 className="m-0 text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">Job board</h1>
                <p className="m-0 mt-1 text-sm text-gray-500 sm:text-base">
                    Open jobs from {laravel.platform_name} clients. Tell clients you’re interested in roles you’ve signed up for, or refer a friend for the rest.
                </p>
            </header>

            {/* Filter bar */}
            <div className="mb-5 flex flex-col gap-3 rounded-2xl bg-white p-3 shadow-[0_1px_3px_rgba(16,24,40,0.06),0_1px_2px_rgba(16,24,40,0.04)] sm:p-4 lg:flex-row lg:items-center">
                <label className={TB.mapClass('flex shrink-0 items-center justify-between gap-3 text-sm font-semibold text-gray-900 lg:justify-start lg:border-0 lg:border-r lg:border-solid lg:border-gray-200 lg:pr-4', {
                    'pointer-events-none opacity-60': controlsDisabled,
                })}>
                    Only my matches
                    <AntSwitch
                        checked={!!filters.only_matching}
                        onChange={toggleOnlyMatching}
                        disabled={controlsDisabled}
                        className={filters.only_matching ? 'bg-green-600' : 'bg-gray-300'}
                    />
                </label>

                <div className="flex flex-wrap items-center gap-2">
                    <JobBoardFilterDropdown
                        label="Role"
                        options={Disciplines}
                        value={filters.disciplines || []}
                        onChange={disciplines => changeFilters({ disciplines })}
                        disabled={controlsDisabled}
                    />
                    <JobBoardFilterDropdown
                        label="Location"
                        options={TimeZones}
                        value={filters.time_zones || []}
                        onChange={time_zones => changeFilters({ time_zones })}
                        disabled={controlsDisabled}
                    />
                    <JobBoardFilterDropdown
                        label="Contract type"
                        options={ContractTypes}
                        value={filters.contract_types || []}
                        onChange={contract_types => changeFilters({ contract_types })}
                        disabled={controlsDisabled}
                    />
                    <Dropdown
                        trigger={['click']}
                        disabled={controlsDisabled}
                        menu={{
                            items: sortItems,
                            selectable: true,
                            selectedKeys: [sortBy],
                            onClick: e => changeFilters({ sort_by: e.key }),
                        }}
                    >
                        <FilterTrigger label="Sort" badge={SORT_SHORT_LABELS[sortBy] || TalentJobBoard.sortTypes[sortBy]} disabled={controlsDisabled} />
                    </Dropdown>
                </div>

                <div className="flex items-center justify-end gap-2 border-0 border-t border-solid border-gray-100 pt-3 lg:ml-auto lg:border-t-0 lg:pt-0">
                    <AppButton variant="link" disabled={controlsDisabled || !canClearFilters} onClick={clearFilters}>
                        Clear filters
                    </AppButton>
                    <AppButton disabled={controlsDisabled || !canApplyFilters} onClick={() => applyFilters()} className="flex-1 sm:flex-none">
                        Search
                    </AppButton>
                </div>
            </div>

            {/* Results header */}
            {!isFetching && !TB.isObject(error) && jobs?.length > 0 && (
                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <p className="m-0 text-sm text-gray-500">
                        Showing <span className="font-semibold text-gray-900">{showingFrom}–{showingTo}</span> of{' '}
                        <span className="font-semibold text-gray-900">{total}</span> job{total === 1 ? '' : 's'}
                    </p>
                    {pagination}
                </div>
            )}

            {/* Results */}
            {isFetching ? (
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3 xl:gap-6" role="status" aria-busy="true">
                    <span className="sr-only">Loading jobs…</span>
                    {TB.range(SKELETON_CARD_COUNT).map(index => (
                        <JobBoardCardSkeleton key={`job-skeleton-${index}`} />
                    ))}
                </div>
            ) : TB.isObject(error) ? (
                <div className="rounded-2xl bg-white">
                    <ErrorBanner {...error} minHeight={320} onRetry={refetch} />
                </div>
            ) : !jobs?.length ? (
                <div className="rounded-2xl bg-white py-16">
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={<span className="text-sm text-gray-500">No Jobs Found</span>}
                    />
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3 xl:gap-6">
                        {jobs.map(job => (
                            <JobBoardCard
                                key={`job-${job.id}`}
                                job={job}
                                disabled={controlsDisabled}
                                onRequest={onRequest}
                                onUpdateJob={updateJob}
                            />
                        ))}
                    </div>

                    {pagination && <div className="mt-6 flex justify-center">{pagination}</div>}
                </>
            )}
        </div>
    );
};

const JobBoardList = (props) => {
    const location = useLocation();

    const {auth} = useAuth();

    if (auth?.isTalentSupplierResource()) {
        return <Navigate to={'/talent/dashboard'} replace={true}/>
    }
    if (auth.isTalent() && !auth.isProfileApproved()) {
        return <PageNotAllowed redirect="/talent/dashboard" message="Your profile must be approved before accessing this page."/>;
    }
    return (
        <>
           {/* <TalentJobBoard location={location}/> */}

           {/* Add New Job Board Here */}
           <TalentJobBoardNew/>
        </>
    )
}

export {
    JobBoardList
};